/**
 * Quick actions: every Finding says what you can do about it, right there. Call or text the person, send a
 * reminder, log the check now, add the fix, find where the supply is kept, or call the supplier.
 * Pure data; each app renders it (tel:/sms: links, buttons to its own screens).
 */
import type { Id } from "./model.ts";

export type SupplyKind = "cleaning" | "paper" | "chemical" | "equipment" | "food";
/** A supply the location keeps on hand: where it lives and who to call for more. */
export interface Supply {
  key: string; // stable, e.g. "hand-soap"
  name: string;
  kind: SupplyKind;
  whereKept?: string; // "Storage room, shelf B"; with spotId it is that storage spot's name (spots.ts keeps it in step)
  /** The storage spot it lives in (spots.ts). Absent on older rows: their whereKept text becomes a spot on the next write. */
  spotId?: string;
  /** "I have this" / "Out of it", said where the product is used (spots.ts). A newer arrival answers an "out" (ordering.ts). */
  stock?: SupplyStock;
  /** A shelf or drawer inside its spot ("Drawer L4", "Shelf 2"), one of the spot's sections (spots.ts). Optional. */
  section?: string;
  /** Where its safety data sheet is: a link, or where the paper copy is (supplyinfo.ts; OSHA 29 CFR 1910.1200(g)). */
  sds?: { url?: string; where?: string; at: string; by?: Id };
  /**
   * "Is it below and away from food?" as a person answered it standing at the shelf (chemicals.ts; Food Code 7-201.11).
   * One tap, with who and when. Cleared when the supply moves to another place: the answer was about the old one.
   */
  storage?: { ok: boolean; at: string; by?: Id };
  /** Protective gear, copied from the label's precautionary statements by a person (never written by the app). */
  ppe?: string;
  /** What it fits, as the person says it ("13-gallon can", "the paper towel dispenser by the hand sink"). */
  fits?: string;
  supplier?: { name: string; phone?: string; email?: string; url?: string };
  note?: string;
  /** From a label photo on the room walk (roomwalk.ts), or typed: the brand, the product line and the size, verbatim. Never guessed. */
  brand?: string;
  product?: string;
  size?: string;
  /** Refill settings (orders.ts). Counts are in `unit`s. */
  unit?: string; // "case of 12", "box of 100"
  par?: number; // keep at least this many
  onHand?: number;
  unitPriceCents?: number;
  /**
   * How often a finished procedure listed it (stepsupplies.ts): uses per week, by the week's Monday (UTC), the last 12
   * weeks. A count for the SUPPLY, never per person (G2b: no one's work is measured by what they used).
   */
  uses?: Record<string, number>;
  /** Uses each earlier stocking lasted (have → low or out), the last 5: what "running low soon" is predicted from. */
  lasted?: number[];
}

export interface SupplyStock { state: "have" | "low" | "out"; at: string; by?: Id }

/** Starter list. `whereKept` and suppliers are filled in by the owner in Settings. */
export const SUPPLY_SEEDS: Supply[] = [
  { key: "hand-soap", name: "Hand soap", kind: "paper" },
  { key: "paper-towels", name: "Paper towels", kind: "paper" },
  { key: "toilet-paper", name: "Toilet paper", kind: "paper" },
  { key: "trash-bags", name: "Trash bags", kind: "paper" },
  { key: "gloves", name: "Single-use gloves", kind: "paper" },
  { key: "sanitizer", name: "Food-contact sanitizer", kind: "chemical", note: "EPA-registered for food-contact surfaces" },
  { key: "test-strips", name: "Sanitizer test strips", kind: "chemical", note: "Match the sanitizer: chlorine or quat" },
  { key: "disinfectant", name: "Restroom disinfectant", kind: "chemical", note: "EPA-registered, norovirus claim" },
  { key: "bleach", name: "Unscented bleach", kind: "chemical", note: "Vomit/diarrhea cleanup: 1,000–5,000 ppm" },
  { key: "restroom-mop", name: "Restroom mop, bucket and cloths (labeled)", kind: "cleaning", note: "Never used in the kitchen" },
  { key: "kitchen-mop", name: "Kitchen mop, bucket and cloths (labeled)", kind: "cleaning", note: "Never used in the restroom" },
  { key: "wiping-cloths", name: "Wiping cloths", kind: "cleaning" },
  { key: "thermometer", name: "Probe thermometer", kind: "equipment" },
  // Cleaning products named in the source protocols' product tables (examples, not endorsements).
  { key: "dish-soap", name: "Dish soap", kind: "cleaning", note: "e.g. Dawn Professional or Dawn Ultra" },
  { key: "dish-detergent", name: "Dishwasher detergent", kind: "cleaning", note: "Commercial: from your machine's service company" },
  { key: "degreaser", name: "Degreaser (grease, ovens, hoods)", kind: "cleaning", note: "e.g. Dawn Powerwash, Simple Green Pro HD" },
  { key: "stainless-cleaner", name: "Stainless steel cleaner", kind: "cleaning", note: "e.g. Weiman; Bar Keepers Friend for scrubbing" },
  { key: "glass-cleaner", name: "Glass cleaner", kind: "cleaning", note: "Never on food-contact surfaces" },
  { key: "stone-cleaner", name: "Granite and stone counter cleaner", kind: "cleaning" },
  { key: "wood-cleaner", name: "Wood table and furniture cleaner", kind: "cleaning", note: "e.g. Murphy Oil Soap, diluted" },
  { key: "floor-cleaner", name: "Floor cleaner (tile, vinyl, kitchen degreaser)", kind: "cleaning" },
  { key: "hardwood-cleaner", name: "Hardwood floor cleaner", kind: "cleaning", note: "e.g. Bona" },
  { key: "upholstery-cleaner", name: "Upholstery and carpet cleaner", kind: "cleaning", note: "Test a hidden spot" },
  { key: "espresso-cleaner", name: "Espresso backflush cleaner", kind: "cleaning", note: "e.g. Urnex Cafiza" },
  { key: "wand-cleaner", name: "Steam wand cleaner", kind: "cleaning", note: "e.g. Urnex Rinza" },
  { key: "descaler", name: "Espresso machine descaler", kind: "cleaning", note: "e.g. Urnex Dezcal, or the maker's" },
  { key: "grinder-cleaner", name: "Grinder cleaning tablets", kind: "cleaning", note: "e.g. Urnex Grindz" },
  { key: "alcohol-swabs", name: "70% alcohol swabs (thermometer probes)", kind: "cleaning" },
  { key: "nail-brush", name: "Nail brush (one per handwashing sink)", kind: "cleaning" },
];

/** Which supplies fix which failed rule (template rule ids). */
export const RULE_SUPPLIES: Record<string, string[]> = {
  rr_soap: ["hand-soap"], rr_towels: ["paper-towels"], rr_tissue: ["toilet-paper"], rr_trash: ["trash-bags"],
  rr_clean: ["disinfectant", "restroom-mop"],
  chlorine: ["sanitizer", "test-strips"], quat: ["sanitizer", "test-strips"], iodine: ["sanitizer", "test-strips"],
};

export type ActionKind = "call" | "text" | "remind" | "do" | "fix" | "open" | "locate" | "reorder";
export interface QuickAction {
  kind: ActionKind;
  label: string;
  /** For call/text/reorder: a ready tel:, sms: or https: link. */
  href?: string;
  /** What the app should open or act on. */
  target?: { type: "employee" | "check" | "entry" | "incident" | "review" | "supply"; id: Id };
  /** Extra line, e.g. where a supply is kept. */
  detail?: string;
}

const digits = (p: string) => p.replace(/[^\d+]/g, "");

export function personActions(p: { id: Id; name: string; phone?: string }, message: string): QuickAction[] {
  const first = firstName(p.name);
  const out: QuickAction[] = [];
  if (p.phone) {
    out.push({ kind: "call", label: `Call ${first}`, href: `tel:${digits(p.phone)}`, target: { type: "employee", id: p.id } });
    out.push({ kind: "text", label: `Text ${first}`, href: `sms:${digits(p.phone)}?&body=${encodeURIComponent(message)}`, target: { type: "employee", id: p.id } });
  }
  out.push({ kind: "remind", label: `Remind ${first}`, target: { type: "employee", id: p.id }, detail: message });
  return out;
}

export function supplyActions(ruleIds: string[], supplies: Supply[] = []): QuickAction[] {
  const keys = [...new Set(ruleIds.flatMap((r) => RULE_SUPPLIES[r] ?? []))];
  const out: QuickAction[] = [];
  for (const key of keys) {
    const s = supplies.find((x) => x.key === key) ?? SUPPLY_SEEDS.find((x) => x.key === key);
    if (!s) continue;
    out.push({ kind: "locate", label: `Where's the ${s.name.toLowerCase()}?`, detail: s.whereKept ?? "Not set yet: add where it's kept in Settings.", target: { type: "supply", id: s.key } });
    if (s.supplier?.phone) out.push({ kind: "reorder", label: `Call ${s.supplier.name}`, href: `tel:${digits(s.supplier.phone)}`, detail: `Reorder ${s.name.toLowerCase()}`, target: { type: "supply", id: s.key } });
    else if (s.supplier?.url) out.push({ kind: "reorder", label: `Reorder from ${s.supplier.name}`, href: s.supplier.url, target: { type: "supply", id: s.key } });
  }
  return out;
}

/** The name to greet or address someone by: the first word, skipping a title ("Fr. Tom Reilly" → "Tom", "Sam Ortiz" → "Sam"). */
export function firstName(name: string): string {
  const words = name.replace(/\s*\(.*\)\s*$/, "").trim().split(/\s+/).filter(Boolean);
  const titled = words.length > 1 && /^(fr|father|sr|sister|br|brother|rev|pastor|bishop|elder|mr|mrs|ms|mx|dr)\.?$/i.test(words[0]!);
  return (titled ? words[1] : words[0]) ?? name;
}
